
import React, {useState, useContext, useEffect} from 'react'
import {SpinnerComponent} from 'react-element-spinner'
import { useTranslation } from 'react-i18next'
import { Button, Card, CardBody, CardHeader, Col, Container, Form, FormGroup, Input, Label, Row } from 'reactstrap'

import AdministrationService from '../../services/AdministrationService'
import { SessionContext } from '../../contexts/SessionProvider'
import SiteService from '../../services/SiteService'
import { trigger } from '../../events/customEvents'
import PageTitle from '../../components/PageTitles/PageTitle'

const AuctionDurations = () =>
{
	const { t } = useTranslation()
	const [isBusy, setIsBusy] = useState(false)
	const [auctionDurations, setAuctionDurations] = useState({
		tierActiveDurationHours: 22,
		tierMbpDurationHours: 24,
		tierStartHourMilitary: 17,
		tierEndHourMilitary: 15,
		settlementDurationHours: 3,
		maxAuctionTierBiddingExtensions: 2,
		validation: {
			tierActiveDurationHours: true,
			tierMbpDurationHours: true,
			tierStartHourMilitary: true,
			tierEndHourMilitary: true,
			settlementDurationHours: true,
			maxAuctionTierBiddingExtensions: true
		}
	})
	const sessionContext = useContext(SessionContext)
	const [formSubmitted, setFormSubmitted] = useState(false)
	
	useEffect(() =>
	{
		retrieveDurations()
	}, [])
	
	const retrieveDurations = () =>
	{
		setIsBusy(true)
		AdministrationService
			.getDurations(sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				let durations = result.data.actionResult
				const validation =
				{
					validation:
					{
						tierActiveDurationHours: (!isNaN(durations.tierActiveDurationHours) && parseInt(durations.tierMbpDurationHours) > 0),
						tierMbpDurationHours: (!isNaN(durations.tierMbpDurationHours) && parseInt(durations.tierMbpDurationHours) > 0),
						tierStartHourMilitary: (!isNaN(durations.tierStartHourMilitary) && parseInt(durations.tierStartHourMilitary) > 0),
						tierEndHourMilitary: (!isNaN(durations.tierEndHourMilitary) && parseInt(durations.tierEndHourMilitary) > 0),
						settlementDurationHours: (!isNaN(durations.settlementDurationHours) && parseInt(durations.settlementDurationHours) > 0),
						maxAuctionTierBiddingExtensions: (!isNaN(durations.maxAuctionTierBiddingExtensions) && parseInt(durations.maxAuctionTierBiddingExtensions) > 0)
					}
				}
				
				setAuctionDurations({
					...durations,
					...validation
				})
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handlePropertyChange = (e) =>
	{
		const { name, value} = e.target
		setAuctionDurations((previousAuctionDuration) =>
		{
			const currentAuctionDurations = { ...previousAuctionDuration }
			currentAuctionDurations[name] = value
			currentAuctionDurations.validation[name] = (!isNaN(value) && parseInt(value) > 0)
			return currentAuctionDurations
		})
	}
	
	const isValidDurations = () =>
	{
		let durationsValid = true
		for (const [key, value] of Object.entries(auctionDurations.validation))
		{
			if (!value)
			{
				durationsValid = false
				break
			}
		}
		
		return durationsValid
	}
	
	const handleSaveDurations = () =>
	{
		setFormSubmitted(true)
		if (isValidDurations())
		{
			setIsBusy(true)
			AdministrationService
				.saveDurations(auctionDurations, sessionContext.loginSession.user.userSession.sessionKey)
				.then(result =>
				{
					setIsBusy(false)
					sessionContext.updateSession(result.data.loginSession)
					
					setFormSubmitted(false)
					trigger('raiseNotification', { title: 'Update Success', message: 'The auction durations were updated.', type: 'success', duration: 5 })
				})
				.catch((error) =>
				{
					setIsBusy(false)
					setFormSubmitted(false)
					SiteService.displayApiError(error)
				})
		}
		else
		{
			trigger('raiseNotification', { title: 'Input Error', message: 'The input is invalid.', type: 'danger ', duration: 5 })
		}
	}
	
	return (
		<>
			<SpinnerComponent
				loading={ isBusy }
				position="global"
				color="#3578e5"
				secondaryColor="#f3f3f3"
				backgroundColor="#d3d3d3"
				spinnerType="circle-dots"
			/>
			<PageTitle
				heading={ t('Auctions.Durations.Title') }
				subheading=""
				icon="pe-7s-tools icon-gradient bg-premium-dark"
			/>
			<Container className="mt--6" fluid>
				<Row className="justify-content-center">
					<Col className="card-wrapper ct-example" lg="8">
						<Card>
							<CardHeader></CardHeader>
							<CardBody>
								<Form>
									<FormGroup className="row">
										<Label
											className="form-control-label text-right"
											htmlFor="tierActiveDurationHours"
											md="6"
										>
											{ t('Auctions.Durations.Default_Duration_Hours') }
										</Label>
										<Col md="6">
											<Input
												name="tierActiveDurationHours"
												type="text"
												value={ auctionDurations.tierActiveDurationHours }
												onChange={ handlePropertyChange }
												valid={ formSubmitted && auctionDurations.validation.tierActiveDurationHours }
												invalid={ formSubmitted && !auctionDurations.validation.tierActiveDurationHours }
											/>
										</Col>
									</FormGroup>
									<FormGroup className="row">
										<Label
											className="form-control-label text-right"
											htmlFor="tierMbpDurationHours"
											md="6"
										>
											{ t('Auctions.Durations.Mbp_Duration_Hours') }
										</Label>
										<Col md="6">
											<Input
												name="tierMbpDurationHours"
												type="text"
												value={ auctionDurations.tierMbpDurationHours }
												onChange={ handlePropertyChange }
												valid={ formSubmitted && auctionDurations.validation.tierMbpDurationHours }
												invalid={ formSubmitted && !auctionDurations.validation.tierMbpDurationHours }
											/>
										</Col>
									</FormGroup>
									<FormGroup className="row">
										<Label
											className="form-control-label text-right"
											htmlFor="tierStartHourMilitary"
											md="6"
										>
											{ t('Auctions.Durations.Tier_Military_Start_Hour') }
										</Label>
										<Col md="6">
											<Input
												name="tierStartHourMilitary"
												type="text"
												value={ auctionDurations.tierStartHourMilitary }
												onChange={ handlePropertyChange }
												valid={ formSubmitted && auctionDurations.validation.tierStartHourMilitary }
												invalid={ formSubmitted && !auctionDurations.validation.tierStartHourMilitary }
											/>
										</Col>
									</FormGroup>
									<FormGroup className="row">
										<Label
											className="form-control-label text-right"
											htmlFor="tierEndHourMilitary"
											md="6"
										>
											{ t('Auctions.Durations.Tier_Military_End_Hour') }
										</Label>
										<Col md="6">
											<Input
												name="tierEndHourMilitary"
												type="text"
												value={ auctionDurations.tierEndHourMilitary }
												onChange={ handlePropertyChange }
												valid={ formSubmitted && auctionDurations.validation.tierEndHourMilitary }
												invalid={ formSubmitted && !auctionDurations.validation.tierEndHourMilitary }
											/>
										</Col>
									</FormGroup>
									<FormGroup className="row">
										<Label
											className="form-control-label text-right"
											htmlFor="settlementDurationHours"
											md="6"
										>
											{ t('Auctions.Durations.Settlement_Duration_Hours') }
										</Label>
										<Col md="6">
											<Input
												name="settlementDurationHours"
												type="text"
												value={ auctionDurations.settlementDurationHours }
												onChange={ handlePropertyChange }
												valid={ formSubmitted && auctionDurations.validation.settlementDurationHours }
												invalid={ formSubmitted && !auctionDurations.validation.settlementDurationHours }
											/>
										</Col>
									</FormGroup>
									<FormGroup className="row">
										<Label
											className="form-control-label text-right"
											htmlFor="maxAuctionTierBiddingExtensions"
											md="6"
										>
											{ t('Auctions.Durations.Max_Tier_Bidding_Extensions') }
										</Label>
										<Col md="6">
											<Input
												name="maxAuctionTierBiddingExtensions"
												type="text"
												value={ auctionDurations.maxAuctionTierBiddingExtensions }
												onChange={ handlePropertyChange }
												valid={ formSubmitted && auctionDurations.validation.maxAuctionTierBiddingExtensions }
												invalid={ formSubmitted && !auctionDurations.validation.maxAuctionTierBiddingExtensions }
											/>
										</Col>
									</FormGroup>
									<Button
										color="primary"
										size="lg"
										block
										className="btn-shadow btn-hover-shine btn-wide mb-3"
										onClick={ handleSaveDurations }
									>
										Save
									</Button>
								</Form>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</Container>
		</>
	)
}

export default AuctionDurations
